import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArticleGenerationRow, ArticleMode, ArticlePart } from '../../types/articleGeneration';
import { GenerationProgress, progressFromRow } from './progressReducer';
import GenerationStatusPanel from './GenerationStatusPanel';
import { effectiveStatus } from '../ArticleHistory/RunList';
import ArticleResult from './ArticleResult';
import HeadingsResult from './HeadingsResult';
import CritiqueTimeline from './CritiqueTimeline';
import CommentsPanel from './CommentsPanel';
import ArticleInputs from './ArticleInputs';
import LogPanel from './LogPanel';
import ExportButtons from './export/ExportButtons';
import SeoBlock from './SeoBlock';
import { runTitle } from '../ArticleHistory/RunList';

interface Props {
  run: ArticleGenerationRow | null;
  runs: ArticleGenerationRow[];
  isGenerating: boolean;
  progress: GenerationProgress | null;
  activeMode: ArticleMode | null;
  elapsedMs: number;
  onCancel: () => void;
  /** Stop a run that continues server-side (chunked execution). */
  onStop?: (run: ArticleGenerationRow) => void;
  onContinue: (run: ArticleGenerationRow) => void;
  onRecritique: (run: ArticleGenerationRow) => void;
  onRegeneratePart: (part: ArticlePart, note: string) => void;
  onNoteSaved: (patch: Partial<ArticleGenerationRow>) => void;
  onPatched: (patch: Partial<ArticleGenerationRow>) => void;
  /** Start a full article that keeps this plan's headings (headings / briefs runs). */
  onWriteArticle?: (run: ArticleGenerationRow) => void;
  /** Focus the workspace on another run (the plan a seeded article came from). */
  onOpenRun?: (id: string) => void;
  /** Opened from Generation history — reveal the original inputs up front. */
  historyView?: boolean;
  /** Open the article's LLM log (every model call of this generation) at once. */
  initialLogOpen?: boolean;
}

// A run has something to critique once its artifact exists. A full-article run
// paused at the human checkpoint has a plan (skeleton) to critique again.
const canRecritique = (run: ArticleGenerationRow): boolean =>
  run.mode === 'full_article'
    ? (run.sections?.some((s) => s.status === 'done') ?? false) ||
      (run.status === 'needs_review' && run.structure != null)
    : run.structure != null;

// The human checkpoint. With text written, the critics did not accept every
// section within the budget; without text (legacy runs) the plan was not approved.
const atReview = (run: ArticleGenerationRow): boolean =>
  run.mode === 'full_article' && run.status === 'needs_review';
const hasText = (run: ArticleGenerationRow): boolean =>
  (run.sections ?? []).some((s) => s.status === 'done');
// Something to download: a plan with headings, or an article with written text.
const hasArtifact = (run: ArticleGenerationRow): boolean =>
  run.mode === 'full_article' ? hasText(run) : run.structure != null;

const statusTone: Record<string, string> = {
  done: 'bg-green-100 text-green-800',
  generating: 'bg-blue-100 text-blue-800',
  needs_review: 'bg-amber-100 text-amber-900',
  incomplete: 'bg-amber-100 text-amber-800',
  cancelled: 'bg-gray-100 text-gray-700',
  error: 'bg-red-100 text-red-800',
};

const ArticleWorkspace: React.FC<Props> = ({
  run,
  runs,
  isGenerating,
  progress,
  activeMode,
  elapsedMs,
  onCancel,
  onStop,
  onContinue,
  onRecritique,
  onRegeneratePart,
  onNoteSaved,
  onPatched,
  onWriteArticle,
  onOpenRun,
  historyView = false,
  initialLogOpen = false,
}) => {
  const { t } = useTranslation();
  // Every model call of this generation, shown only on demand from the details.
  const [logOpen, setLogOpen] = useState(initialLogOpen);

  if (!run && !isGenerating) {
    return (
      <div className="border rounded-lg p-10 text-center text-gray-500 bg-gray-50">
        {t('article.workspace_empty')}
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {isGenerating && progress && activeMode && (
        <GenerationStatusPanel
          mode={activeMode}
          progress={progress}
          elapsedMs={elapsedMs}
          runs={runs}
          onCancel={onCancel}
        />
      )}

      {/* Polled/reconnected run still generating (this client isn't streaming
          it): show live status rebuilt from the persisted progress. */}
      {run && !isGenerating && effectiveStatus(run) === 'generating' && (
        <GenerationStatusPanel
          mode={run.mode}
          progress={progressFromRow(run.progress, run.inputs.headingCount)}
          elapsedMs={Math.max(0, Date.now() - new Date(run.created_at).getTime())}
          runs={runs}
          onCancel={() => {}}
          onStop={onStop ? () => onStop(run) : undefined}
          variant="inline"
        />
      )}

      {run && (
        <>
          <div className="flex items-start justify-between flex-wrap gap-2">
            <div>
              <h2 className="text-xl font-bold">{runTitle(run)}</h2>
              {run.inputs.planFrom && (
                <div className="mt-1 text-xs text-gray-600">
                  <button
                    type="button"
                    onClick={() => onOpenRun?.(run.inputs.planFrom!.generationId)}
                    className="text-blue-600 hover:underline"
                  >
                    {t('article.based_on_plan')}
                  </button>
                </div>
              )}
              <div className="mt-1 text-xs text-gray-500">
                {t('article.workspace_created', {
                  created: new Date(run.created_at).toLocaleString(),
                })}
                {run.updated_at && run.updated_at !== run.created_at && (
                  <>
                    {' · '}
                    {t('article.workspace_updated', {
                      updated: new Date(run.updated_at).toLocaleString(),
                    })}
                  </>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setLogOpen((o) => !o)}
                aria-pressed={logOpen}
                className="text-sm text-blue-600 hover:underline"
              >
                {t('article.llm_log_link')}
              </button>
              {!isGenerating && effectiveStatus(run) !== 'generating' && canRecritique(run) && (
                <button
                  type="button"
                  onClick={() => onRecritique(run)}
                  className="px-3 py-1.5 border border-blue-600 text-blue-600 rounded-md text-sm hover:bg-blue-50"
                >
                  {t('article.run_critique_again')}
                </button>
              )}
              <span
                className={`text-xs px-2 py-1 rounded-full ${statusTone[effectiveStatus(run)] ?? statusTone.cancelled}`}
              >
                {t(`article.status_${effectiveStatus(run)}`, effectiveStatus(run))}
              </span>
            </div>
          </div>

          {!isGenerating &&
            !atReview(run) &&
            (() => {
              const outcomes = Object.values(run.critique ?? {}).map((c) => c.outcome);
              const bad = outcomes.includes('inconclusive')
                ? 'inconclusive'
                : outcomes.includes('unresolved')
                  ? 'unresolved'
                  : outcomes.includes('publishable')
                    ? 'publishable'
                    : null;
              return bad ? (
                <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800">
                  {t(`article.critique_banner_${bad}`)}
                </div>
              ) : null;
            })()}

          {logOpen && (
            <section className="border rounded-lg p-4 bg-gray-50">
              <LogPanel
                generationId={run.id}
                run={run}
                live={isGenerating || effectiveStatus(run) === 'generating'}
              />
            </section>
          )}

          <ArticleInputs run={run} defaultOpen={historyView} />

          {hasArtifact(run) && (
            <ExportButtons
              run={run}
              disabled={isGenerating || effectiveStatus(run) === 'generating'}
            />
          )}

          {hasArtifact(run) && <SeoBlock seo={run.seo} />}

          {atReview(run) && hasText(run) ? (
            <section className="space-y-4" data-testid="article-review">
              <div className="rounded-md border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
                <div className="font-semibold mb-1">{t('article.needs_review_article_title')}</div>
                <p>
                  {t('article.needs_review_article_banner', {
                    sections: (run.critique?.article?.unaccepted ?? []).join(', ') || '-',
                    rounds: run.critique?.article?.rounds ?? 0,
                  })}
                </p>
                {run.critique?.article?.reason === 'critique_error' && (
                  <p className="mt-1" data-testid="critique-error-note">
                    {t('article.needs_review_article_error')}
                  </p>
                )}
              </div>
              <ArticleResult
                run={run}
                onNoteSaved={onNoteSaved}
                onRegeneratePart={onRegeneratePart}
                onContinue={() => onContinue(run)}
                isGenerating={isGenerating}
              />
            </section>
          ) : atReview(run) ? (
            <section className="space-y-4">
              <div className="rounded-md border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
                <div className="font-semibold mb-1">{t('article.needs_review_title')}</div>
                <p>
                  {t('article.needs_review_banner', {
                    sections: (run.critique?.skeleton?.unaccepted ?? []).join(', ') || '-',
                    rounds: run.critique?.skeleton?.rounds ?? 0,
                  })}
                </p>
              </div>
              <HeadingsResult run={run} onPatched={onPatched} />
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => onContinue(run)}
                  disabled={isGenerating}
                  className="px-3 py-1.5 bg-blue-600 text-white rounded-md text-sm disabled:opacity-50"
                >
                  {t('article.continue_generation')}
                </button>
              </div>
            </section>
          ) : run.mode === 'full_article' ? (
            <ArticleResult
              run={run}
              onNoteSaved={onNoteSaved}
              onRegeneratePart={onRegeneratePart}
              onContinue={() => onContinue(run)}
              isGenerating={isGenerating}
            />
          ) : (
            <HeadingsResult
              run={run}
              onPatched={onPatched}
              onWriteArticle={
                onWriteArticle &&
                !isGenerating &&
                effectiveStatus(run) !== 'generating' &&
                run.structure != null
                  ? () => onWriteArticle(run)
                  : undefined
              }
            />
          )}

          <section>
            <h3 className="font-semibold mb-3">{t('article.critique_timeline_title')}</h3>
            <CritiqueTimeline generationId={run.id} critique={run.critique} />
          </section>

          <CommentsPanel run={run} />
        </>
      )}
    </div>
  );
};

export default ArticleWorkspace;
